// Add or edit a court-rental customer.
//
// Shared by People → Customers (the Add button and the row kebab) and by the
// Edit button on the Company tab of a renter's profile, which renders
// CompanyPanel. One form, so the billing-rate rules — a shared rate from
// Court Rentals, or a custom rate stored on the customer doc itself — can't
// drift between the two places a customer gets edited.
//
// Self-contained: loads its own rental rates and owns its save, handing the
// written doc back through onSaved so the caller can refresh its own copy.
import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, doc, query, where, addDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { CUSTOM_RATE } from '../utils/billingRates';
import { logError } from '../utils/errorLogger';
import {
  Box, TextField, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';

// billingRateId '' = none, a rentalRates doc id = shared rate, CUSTOM_RATE = a
// per-customer rate stored on the customer doc itself.
const EMPTY = {
  organizationName: '', firstName: '', lastName: '', email: '', phone: '',
  billingRateId: '', customRateName: '', customHourly: '', customFacility: '',
};

const formFor = (cust) => (cust ? {
  organizationName: cust.organizationName || '',
  firstName: cust.firstName || '',
  lastName: cust.lastName || '',
  email: cust.email || '',
  phone: cust.phone || '',
  billingRateId: cust.billingRateId || '',
  customRateName: cust.billingRateName || '',
  customHourly: cust.billingRateHourly != null ? String(cust.billingRateHourly) : '',
  customFacility: cust.billingFacilityRate != null ? String(cust.billingFacilityRate) : '',
} : EMPTY);

export default function CustomerEditDialog({ open, customer, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [rentalRates, setRentalRates] = useState([]);
  const [saving, setSaving] = useState(false);

  // Reseed each time it opens, so a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (open) setForm(formFor(customer));
  }, [open, customer]);

  useEffect(() => {
    if (!open) return;
    getDocs(collection(db, 'rentalRates'))
      .then(s => setRentalRates(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(e => logError('CustomerEditDialog', 'rental rates: ' + (e.message || String(e)), e.stack || ''));
  }, [open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.organizationName.trim()) return;
    setSaving(true);
    try {
      const isCustom = form.billingRateId === CUSTOM_RATE;
      const rate = rentalRates.find(r => r.id === form.billingRateId);
      const { customRateName, customHourly, customFacility, ...formRest } = form;
      const data = {
        ...formRest,
        organizationName: form.organizationName.trim(),
        billingRateName: isCustom ? (customRateName.trim() || 'Custom rate') : (rate?.rateName || ''),
        billingRateHourly: isCustom
          ? (customHourly === '' ? null : Number(customHourly))
          : (rate?.hourlyRate ?? null),
        // Only a custom rate carries its own facility rate; shared rates keep
        // theirs on the rentalRates doc.
        billingFacilityRate: isCustom && customFacility !== '' ? Number(customFacility) : null,
        updatedAt: serverTimestamp(),
      };
      // Attach the account that shares this email, if there is one. A customer
      // is a billing contact and often never signs in — East River and Timber
      // Creek have no account at all — so the link is opportunistic, and
      // nothing downstream may assume it exists. It is also not unique: two
      // internal customers legitimately point at the same staff account.
      try {
        const em = String(data.email || '').toLowerCase().trim();
        if (em) {
          const uSnap = await getDocs(query(collection(db, 'users'), where('email', '==', em)));
          if (!uSnap.empty) { data.userId = uSnap.docs[0].id; data.userEmail = em; }
        }
      } catch (e) { console.warn('customer/user link lookup failed', e); }

      let saved;
      if (customer?.id) {
        await updateDoc(doc(db, 'customers', customer.id), data);
        saved = { ...customer, ...data };
      } else {
        const ref = await addDoc(collection(db, 'customers'), { ...data, createdAt: serverTimestamp() });
        saved = { id: ref.id, ...data };
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      logError('CustomerEditDialog', 'save customer: ' + (e.message || String(e)), e.stack || '');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{customer?.id ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Organization Name" required fullWidth size="small"
          value={form.organizationName} onChange={set('organizationName')} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="First Name" fullWidth size="small" value={form.firstName} onChange={set('firstName')} />
          <TextField label="Last Name" fullWidth size="small" value={form.lastName} onChange={set('lastName')} />
        </Box>
        <TextField label="Email" type="email" fullWidth size="small" value={form.email} onChange={set('email')} />
        <TextField label="Phone" fullWidth size="small" value={form.phone} onChange={set('phone')} />
        <TextField select label="Billing Rate" fullWidth size="small"
          value={form.billingRateId} onChange={set('billingRateId')}
          helperText="From Court Rentals rates, or set one just for this customer">
          <MenuItem value="">— None —</MenuItem>
          {rentalRates.map(r => (
            <MenuItem key={r.id} value={r.id}>
              {r.rateName}{r.hourlyRate != null && r.hourlyRate !== '' ? ` — $${r.hourlyRate}/hr` : ''}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM_RATE}>— Custom rate for this customer —</MenuItem>
        </TextField>
        {form.billingRateId === CUSTOM_RATE && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1, borderLeft: '2px solid rgba(201,168,76,0.4)' }}>
            <TextField label="Rate name" fullWidth size="small" value={form.customRateName}
              placeholder="Custom rate" onChange={set('customRateName')} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="$ / hour" type="number" fullWidth size="small" value={form.customHourly}
                onChange={set('customHourly')} helperText="Charged per court, per hour" />
              <TextField label="$ / day (all courts)" type="number" fullWidth size="small" value={form.customFacility}
                onChange={set('customFacility')}
                helperText="Optional — flat rate when all 6 courts are booked" />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}
          disabled={saving || !form.organizationName.trim()}
          sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#b8973f' } }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
